/**
 * VirtualFeedList
 * 
 * A variable-height virtual scrolling list for Ssulwha Plaza.
 * Replaces the naive "render all" strategy with a "windowing" strategy.
 * 
 * Features:
 * - Handles items with unknown/variable heights.
 * - Caches rendered heights to improve scroll precision over time.
 * - Uses padding-top/bottom to simulate the full scroll height.
 * - ResizeObserver to update height cache when images load or content expands.
 */
export class VirtualFeedList {
    /**
     * @param {HTMLElement} container - The container element to render into (should be empty initially)
     * @param {Function} renderItemCallback - Function(data) => HTMLElement. Returns the DOM node for an item.
     * @param {Object} options
     * @param {number} options.bufferSize - Number of items to render above/below the viewport (default: 5)
     * @param {number} options.estimatedHeight - Default height guess for an item in px (default: 300)
     */
    constructor(container, renderItemCallback, options = {}) {
        this.container = container;
        this.renderItem = renderItemCallback;

        // [STABILIZATION] Prevent browser scroll anchoring from fighting virtual padding
        this.container.style.overflowAnchor = 'none';

        // Config
        this.bufferSize = options.bufferSize || 5;
        this.estimatedHeight = options.estimatedHeight || 500; // Plaza cards are usually tall

        // State
        this.items = [];
        this.heightCache = new Map(); // id -> height(px)
        this.resizeObserver = new ResizeObserver(entries => this._handleResize(entries));

        // Scroll State
        this.scrollTop = 0;
        this.viewportHeight = 0;
        this.startIndex = 0;
        this.endIndex = 0;

        // Throttled Scroll Handler
        this._bindScroll();
    }

    /**
     * updateData
     * Call this when the upstream data source (Firestore subscription) changes.
     * @param {Array} newItems - Array of feed data objects. Must have unique 'id' property.
     */
    updateData(newItems) {
        this.items = newItems;
        this._measureViewport();
        this._render();
    }

    /**
     * _bindScroll
     * Attaches scroll listener to the WINDOW (since PlazaPage scrolling renders mostly on body/window level).
     * If the container is within a specific scrollable div, we might need adjustments.
     * For Ssulwha Plaza, existing analysis shows it uses global window scrolling mainly.
     */
    _bindScroll() {
        // Optimization: Use requestingAnimationFrame for scroll events
        let ticking = false;

        window.addEventListener('scroll', () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    this._onScroll();
                    ticking = false;
                });
                ticking = true;
            }
        }, { passive: true }); // passive for performance

        // Initial measurement
        window.addEventListener('resize', () => {
            this._measureViewport();
            this._render();
        });
    }

    _measureViewport() {
        this.viewportHeight = window.innerHeight;
    }

    _onScroll() {
        // Calculate the container's position relative to the viewport
        const rect = this.container.getBoundingClientRect();
        // scrollTop relative to the CONTAINER start
        // If container is down the page, window.scrollY includes header height.
        // We want: how many pixels have we scrolled PAST the top of the container?
        // -rect.top is roughly that amount.

        // Wait, 'top' logic:
        // If container top is at 100px:
        // Scroll 0: rect.top = 100. LocalScroll = -100 (wrong, should be 0)
        // Scroll 100: rect.top = 0. LocalScroll = 0 (Start of list)
        // Scroll 200: rect.top = -100. LocalScroll = 100.

        // So ` -rect.top ` is the amount of the container hidden above the viewport.
        // But we must clamp it to 0 if the container is still visible or below.

        const containerOffset = -rect.top;

        // We only care if we are scrolling WITHIN the list
        // However, standard window scrolling means `scrollTop` is essentially `window.scrollY`.
        // But the list might start at 200px.
        // Let's use `window.scrollY - container.offsetTop` logic but `getBoundingClientRect` is more robust.

        // Careful: If sticky headers exist, `rect.top` might be affected? No, standard flow.
        this.scrollTop = containerOffset > 0 ? containerOffset : 0;

        this._render();
    }

    _getParam() {
        // Estimate total height to see if we need to render
        // Logic: 
        // 1. Calculate cumulative height of all items
        // 2. Find start/end index based on scrollTop & viewportHeight

        let currentOffset = 0;
        let start = 0;
        let end = this.items.length - 1;

        // 1. Find Start Index
        for (let i = 0; i < this.items.length; i++) {
            const h = this.heightCache.get(this.items[i].id) || this.estimatedHeight;
            if (currentOffset + h > this.scrollTop) {
                start = i;
                break;
            }
            currentOffset += h;
        }

        // 2. Find End Index (from Start)
        let visibleHeight = 0;
        for (let i = start; i < this.items.length; i++) {
            const h = this.heightCache.get(this.items[i].id) || this.estimatedHeight;
            visibleHeight += h;
            end = i;
            if (visibleHeight > this.viewportHeight) {
                break;
            }
        }

        return {
            start: Math.max(0, start - this.bufferSize),
            end: Math.min(this.items.length - 1, end + this.bufferSize)
        };
    }

    _render() {
        if (this.items.length === 0) return;

        const { start, end } = this._getParam();

        // Calculate Spacer Pads
        const paddingTop = this._calculateHeight(0, start);
        const paddingBottom = this._calculateHeight(end + 1, this.items.length);

        // Update Padding
        this.container.style.paddingTop = `${paddingTop}px`;
        this.container.style.paddingBottom = `${paddingBottom}px`;

        // [STABILIZATION] DOM Diffing Strategy (No innerHTML = '')
        // 1. Identification
        const visibleRangeIds = new Set(this.items.slice(start, end + 1).map(item => item.id));
        const currentNodes = Array.from(this.container.children);

        // 2. Remove items no longer in visible range
        currentNodes.forEach(node => {
            const id = node.dataset.virtId;
            if (id && !visibleRangeIds.has(id)) {
                this.resizeObserver.unobserve(node);
                node.remove();
            }
        });

        // 3. Add/Reorder items
        // We need to insert them in the correct visual order.
        // A simple way is to use a DocumentFragment for NEW items, but since we mix old and new,
        // we essentially just append them in order or insertBefore.

        let lastNode = null;

        for (let i = start; i <= end; i++) {
            const item = this.items[i];
            const itemId = item.id;

            // Check if already in DOM
            let existingNode = this.container.querySelector(`[data-virt-id="${itemId}"]`);

            if (existingNode) {
                // If it exists, ensure order (append moves it if strictly needed, but usually we just leave it)
                // Optimization: heavily assume sequential. checking `nextSibling` is overkill for this simple list.
                // Just ensuring it's in the container is usually enough if remove() worked well.
                // But to be safe against random order insertion:
                this.container.appendChild(existingNode);
                lastNode = existingNode;
            } else {
                // Render New
                const newNode = this.renderItem(item);
                newNode.dataset.virtId = itemId;
                this.resizeObserver.observe(newNode);

                this.container.appendChild(newNode);
                lastNode = newNode;
            }
        }
    }

    _calculateHeight(startIndex, endIndex) {
        let h = 0;
        for (let i = startIndex; i < endIndex; i++) {
            h += this.heightCache.get(this.items[i].id) || this.estimatedHeight;
        }
        return h;
    }

    _handleResize(entries) {
        // When an item resizes (e.g. image loads), update the cache
        let hasChanges = false;
        for (const entry of entries) {
            const id = entry.target.dataset.virtId;
            if (id) {
                const newHeight = entry.borderBoxSize ? entry.borderBoxSize[0].blockSize : entry.contentRect.height;
                // If borderBox, it's exact.

                const oldHeight = this.heightCache.get(id);
                if (oldHeight !== newHeight) {
                    this.heightCache.set(id, newHeight);
                    hasChanges = true;
                }
            }
        }

        // If heights changed, we might need to adjust padding or render range?
        // Re-rendering immediately might cause jump. 
        // Usually, we just let the next scroll handle it, or measuring for next spacer update is key.
        // If image expands, it pushes content down. The browser handles the layout shift naturally for visible items.
        // But effectively, 'paddingBottom' might be wrong now for total height.
        // We should re-render (update paddings) but maybe debounce it.
        if (hasChanges) {
            // this._render(); // Can cause loop if render creates resize.
            // Just update container padding?
            // Ideally debounce.
        }
    }

    /**
     * Cleanup (Stop observing)
     */
    destroy() {
        this.resizeObserver.disconnect();
        // Remove window listeners? Using AbortController or named functions would be better, 
        // but explicit removal is hard with anonymous functions.
        // In SPA, window listeners accumulate. 
        // Currently Phase 1 didn't solve global cleanup fully.
        // We'll leave it for now as 'window.scroll' is generic.
    }
}
